/**
 * Client Hooks
 *
 * React Query hooks for client operations.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import type {
  Client,
  ClientCreate,
  ClientUpdate,
  ClientFilters,
  PaginatedResponse,
} from '@/types'

const CLIENTS_KEY = 'clients'

export function useClients(filters: ClientFilters = {}) {
  return useQuery({
    queryKey: [CLIENTS_KEY, filters],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (filters.search) params.append('search', filters.search)
      if (filters.price_level) params.append('price_level', filters.price_level)
      if (filters.with_credit) params.append('with_credit', 'true')
      if (filters.is_active !== undefined) {
        params.append('is_active', String(filters.is_active))
      }
      if (filters.show_deleted) {
        params.append('show_deleted', 'true')
      }
      if (filters.skip) params.append('skip', String(filters.skip))
      if (filters.limit) params.append('limit', String(filters.limit))

      const response = await api.get<PaginatedResponse<Client>>(
        `/clients?${params.toString()}`
      )
      return response.data
    },
  })
}

export function useClient(id: number) {
  return useQuery({
    queryKey: [CLIENTS_KEY, id],
    queryFn: async () => {
      const response = await api.get<Client>(`/clients/${id}`)
      return response.data
    },
    enabled: !!id,
  })
}

export function useClientByIdentification(identification: string) {
  return useQuery({
    queryKey: [CLIENTS_KEY, 'identification', identification],
    queryFn: async () => {
      const response = await api.get<Client>(
        `/clients/identification/${identification}`
      )
      return response.data
    },
    enabled: !!identification,
  })
}

export function useCreateClient() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: ClientCreate) => {
      const response = await api.post<Client>('/clients', data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CLIENTS_KEY] })
      // El buscador de clientes del POS usa su propia query key; sin esto, la
      // facturación sigue viendo datos viejos del cliente tras crear/editar.
      queryClient.invalidateQueries({ queryKey: ['client-search'] })
    },
  })
}

export function useUpdateClient() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: ClientUpdate }) => {
      const response = await api.put<Client>(`/clients/${id}`, data)
      return response.data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [CLIENTS_KEY] })
      queryClient.invalidateQueries({ queryKey: [CLIENTS_KEY, variables.id] })
      queryClient.invalidateQueries({ queryKey: ['client-search'] })
    },
  })
}

export function useDeleteClient() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, permanent = false }: { id: number; permanent?: boolean }) => {
      await api.delete(`/clients/${id}${permanent ? '?permanent=true' : ''}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CLIENTS_KEY] })
    },
  })
}

export function useRestoreClient() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await api.post<Client>(`/clients/${id}/restore`)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CLIENTS_KEY] })
    },
  })
}

export function useClientCredit(id: number) {
  return useQuery({
    queryKey: [CLIENTS_KEY, id, 'credit'],
    queryFn: async () => {
      const response = await api.get<{
        current_credit: number
        credit_limit: number
        available_credit: number
        pending_bills: number
      }>(`/clients/${id}/credit`)
      return response.data
    },
    enabled: !!id,
  })
}
